import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageSquare, Loader2, X, CheckCheck } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * FeedbackCard (Iter-237) — per-listing lead form. Opens a modal with
 * Name/Email/Mobile/Message + "I am ok to be contacted by other
 * advertisers too" checkbox. POSTs to /classifieds/listings/{id}/feedback
 * (anonymous OK, IP rate-limited to 5 per 5 min → 429 shows inline).
 * Sellers see the lead in their My Ads → Feedback inbox and receive an
 * in-app notification.
 */
export default function FeedbackCard({ listingId }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
    contact_ok: false,
  });
  const [rateLimit, setRateLimit] = useState("");
  const openerRef = useRef(null);
  const firstFieldRef = useRef(null);

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        `/classifieds/listings/${listingId}/feedback`,
        form
      );
      return data;
    },
    onSuccess: () => {
      setSent(true);
      setRateLimit("");
      toast.success("Feedback sent — the seller will be notified.");
    },
    onError: (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setRateLimit(
          typeof detail === "string"
            ? detail
            : "Too many messages — please try again in a few minutes."
        );
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't send feedback.");
      }
    },
  });

  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.message.trim() &&
    !mut.isPending;

  const close = () => {
    setOpen(false);
    setSent(false);
    setRateLimit("");
    setForm({ name: "", email: "", mobile: "", message: "", contact_ok: false });
    // Return focus to the opener (Add Feedback button) for keyboard flow.
    if (openerRef.current) openerRef.current.focus();
  };

  // Escape-key closes the modal; when opening, focus the first field.
  // Also trap Tab/Shift+Tab focus inside the dialog so keyboard users
  // can't accidentally reach controls behind the backdrop (a11y — WAI-ARIA
  // modal dialog pattern).
  useEffect(() => {
    if (!open) return;
    const modalEl = () => document.querySelector('[data-testid="feedback-modal"]');
    const focusables = () => {
      const el = modalEl();
      if (!el) return [];
      return Array.from(
        el.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((n) => n.offsetParent !== null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !modalEl()?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => {
      if (firstFieldRef.current) firstFieldRef.current.focus();
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open]);

  return (
    <>
      <section
        data-testid="feedback-card"
        className="rounded-3xl bg-white shadow-card p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[color:var(--brand-orange)]/10 text-[color:var(--brand-orange)] grid place-items-center shrink-0">
            <MessageSquare className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
              Provide Feedback
            </div>
            <div className="text-slate-800 font-display text-lg font-semibold">
              Interested in this listing?
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Send the advertiser a direct enquiry — they&apos;ll get an instant
              notification.
            </p>
          </div>
        </div>
        <button
          type="button"
          ref={openerRef}
          onClick={() => setOpen(true)}
          data-testid="feedback-open-btn"
          className="mt-4 w-full inline-flex items-center justify-center rounded-full py-2.5 font-bold text-sm text-white bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] transition-colors"
        >
          Add Feedback
        </button>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-subtle">
              <div id="feedback-modal-title" className="font-display font-bold text-slate-900">
                Add Feedback
              </div>
              <button
                type="button"
                onClick={close}
                data-testid="feedback-modal-close"
                aria-label="Close"
                className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {sent ? (
              <div
                className="p-6 text-center"
                data-testid="feedback-sent-state"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-600 grid place-items-center">
                  <CheckCheck className="w-7 h-7" />
                </div>
                <div className="mt-3 font-display font-bold text-slate-900 text-lg">
                  Feedback sent
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  The advertiser has been notified. They&apos;ll be in touch soon.
                </p>
                <button
                  type="button"
                  onClick={close}
                  data-testid="feedback-sent-close"
                  className="mt-4 rounded-full bg-[color:var(--brand-blue)] hover:brightness-110 text-white font-semibold px-6 py-2 text-sm"
                >
                  Close
                </button>
              </div>
            ) : (
              <form
                className="p-5 space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canSubmit) mut.mutate();
                }}
              >
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">
                    Your Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    ref={firstFieldRef}
                    data-testid="feedback-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input mt-1"
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">
                    Email Address <span className="text-red-500">*</span>
                  </span>
                  <input
                    data-testid="feedback-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input mt-1"
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Mobile Number</span>
                  <input
                    data-testid="feedback-mobile"
                    type="tel"
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    className="input mt-1"
                    placeholder="Optional"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">
                    Message <span className="text-red-500">*</span>
                  </span>
                  <textarea
                    data-testid="feedback-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="input mt-1 min-h-[96px]"
                    required
                  />
                </label>
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    data-testid="feedback-contact-ok"
                    type="checkbox"
                    checked={form.contact_ok}
                    onChange={(e) =>
                      setForm({ ...form, contact_ok: e.target.checked })
                    }
                    className="mt-0.5"
                  />
                  <span>I am ok to be contacted by other advertisers too.</span>
                </label>

                {rateLimit && (
                  <div
                    role="alert"
                    data-testid="feedback-rate-limit"
                    className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  >
                    {rateLimit}
                  </div>
                )}

                <div className="pt-1 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={close}
                    data-testid="feedback-cancel"
                    className="rounded-full border border-soft bg-white hover:bg-slate-50 text-slate-700 font-semibold px-5 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="feedback-submit"
                    className="rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-2 text-sm inline-flex items-center gap-2"
                  >
                    {mut.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send Feedback"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
